import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { LocationFilter } from '@/shared/components/filters/LocationFilter';
import { ManufacturerFilter } from '@/shared/components/filters/ManufacturerFilter';
import { DateRangeFilter } from '@/shared/components/filters/DateRangeFilter';
import type { DateRangePreset } from '../hooks/useReportFilters';

interface ReportFiltersProps {
  dateFrom: string;
  dateTo: string;
  locationIds: string[];
  manufacturerIds: string[];
  datePreset: DateRangePreset;
  onDatePresetChange: (preset: DateRangePreset) => void;
  onDateFromChange: (date: string) => void;
  onDateToChange: (date: string) => void;
  onLocationIdsChange: (ids: string[]) => void;
  onManufacturerIdsChange: (ids: string[]) => void;
  onReset: () => void;
  showManufacturerFilter?: boolean;
}

const PRESET_LABELS: Record<DateRangePreset, string> = {
  last7: 'Last 7 Days',
  last30: 'Last 30 Days',
  last90: 'Last 90 Days',
  custom: 'Custom Range',
};

export function ReportFilters({
  dateFrom,
  dateTo,
  locationIds,
  manufacturerIds,
  datePreset,
  onDatePresetChange,
  onDateFromChange,
  onDateToChange,
  onLocationIdsChange,
  onManufacturerIdsChange,
  onReset,
  showManufacturerFilter = false,
}: ReportFiltersProps) {
  return (
    <Card className="p-6 mb-6">
      <div className="space-y-4">
        {/* Date Range Presets */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Range
          </label>
          <div className="flex flex-wrap gap-2">
            {(['last7', 'last30', 'last90', 'custom'] as DateRangePreset[]).map(
              (preset) => (
                <Button
                  key={preset}
                  variant={datePreset === preset ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => onDatePresetChange(preset)}
                >
                  {PRESET_LABELS[preset]}
                </Button>
              )
            )}
          </div>
        </div>

        {/* Custom Date Range */}
        {datePreset === 'custom' && (
          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={onDateFromChange}
            onDateToChange={onDateToChange}
          />
        )}

        {/* Location Filter */}
        <div>
          <LocationFilter
            selectedLocationIds={locationIds}
            onChange={onLocationIdsChange}
            multiple
          />
        </div>

        {/* Manufacturer Filter (optional) */}
        {showManufacturerFilter && (
          <div>
            <ManufacturerFilter
              selectedManufacturerIds={manufacturerIds}
              onChange={onManufacturerIdsChange}
            />
          </div>
        )}

        {/* Reset Button */}
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" onClick={onReset}>
            Reset Filters
          </Button>
        </div>
      </div>
    </Card>
  );
}
